import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import SiteFooter from "../components/SiteFooter";
import { Building2 } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

const TYPES = [
  { v: "athlete", label: "Athlete" },
  { v: "agent", label: "Agent / Manager" },
  { v: "club", label: "Club / Academy" },
  { v: "brand", label: "Brand" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefilledType = params.get("as");
  const [form, setForm] = useState({ full_name: "", email: "", password: "", account_type: "athlete" });
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [orgSuggestion, setOrgSuggestion] = useState(null);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (prefilledType && TYPES.find(t => t.v === prefilledType)) {
      setForm(f => ({ ...f, account_type: prefilledType }));
    }
  }, [prefilledType]);

  // Domain-aware org suggestion: only when account_type is brand/agent/club AND
  // the email looks complete. Debounce by 500ms.
  useEffect(() => {
    setOrgSuggestion(null);
    if (!["brand", "agent", "club"].includes(form.account_type)) return;
    if (!form.email || !form.email.includes("@") || form.email.split("@")[1]?.length < 3) return;
    const t = setTimeout(() => {
      api.get(`/orgs/suggest?email=${encodeURIComponent(form.email)}`)
        .then(({ data }) => { if (data?.match) setOrgSuggestion(data.match); })
        .catch(() => {});
    }, 500);
    return () => clearTimeout(t);
  }, [form.email, form.account_type]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!accepted) {
      setError("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      const u = await register({ ...form, terms_accepted: true, privacy_accepted: true });
      if (u?.account_type === "brand") navigate("/onboarding/brand");
      else if (u?.account_type === "agent" || u?.account_type === "club") navigate("/onboarding/agency");
      else navigate("/onboarding");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <div className="px-6 md:px-12 py-6 border-b border-line">
        <Link to="/" className="flex items-center w-fit">
          <BrandLogo variant="wordmark" size={28} showLink={false} />
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="label-eyebrow mb-3">Create account</div>
          <h1 className="font-heading text-4xl uppercase tracking-tight mb-8">Start your sponsorship engine.</h1>
          <form onSubmit={submit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="label-eyebrow block mb-2">Full name</label>
              <input required value={form.full_name} onChange={(e)=>upd("full_name", e.target.value)} data-testid="register-name-input"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input type="email" required value={form.email} onChange={(e)=>upd("email", e.target.value)} data-testid="register-email-input"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white" />
              {orgSuggestion && (
                <div className="mt-3 border border-volt/40 bg-volt/[0.04] px-4 py-3 flex items-center gap-3" data-testid="register-org-suggestion">
                  <Building2 className="w-4 h-4 text-volt shrink-0" />
                  <div className="text-sm">
                    <div>Looks like you work at <span className="text-volt font-semibold">{orgSuggestion.organisation_name}</span>.</div>
                    <div className="text-[10px] uppercase tracking-widest text-ink-muted mt-0.5">You'll join their {orgSuggestion.type} workspace · {orgSuggestion.member_count} {orgSuggestion.member_count === 1 ? "member" : "members"} already</div>
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password (min 6)</label>
              <input type="password" required minLength={6} value={form.password} onChange={(e)=>upd("password", e.target.value)} data-testid="register-password-input"
                className="w-full bg-bg border border-line focus:border-volt outline-none px-4 py-3 text-white" />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">I am a</label>
              <div className="grid grid-cols-2 gap-2">
                {TYPES.map(t => (
                  <button key={t.v} type="button" onClick={()=>upd("account_type", t.v)} data-testid={`register-type-${t.v}`}
                    className={`border px-3 py-2.5 text-xs uppercase tracking-widest ${form.account_type === t.v ? "border-volt text-volt" : "border-line text-ink-muted hover:text-white"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            {error && <div className="text-sm text-[#FF3B30] border border-[#FF3B30]/30 p-3" data-testid="register-error">{error}</div>}

            <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="register-terms-row">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                data-testid="register-terms-checkbox"
                className="mt-1 w-4 h-4 accent-volt cursor-pointer flex-shrink-0"
              />
              <span className="text-sm text-ink-muted leading-relaxed">
                I agree to the{" "}
                <Link to="/terms" target="_blank" rel="noreferrer" className="text-volt hover:underline" data-testid="register-terms-link">Terms of Service</Link>
                {" "}and{" "}
                <Link to="/privacy" target="_blank" rel="noreferrer" className="text-volt hover:underline" data-testid="register-privacy-link">Privacy Policy</Link>.
              </span>
            </label>

            <button type="submit" disabled={loading || !accepted} className="w-full btn-volt disabled:opacity-40 disabled:cursor-not-allowed" data-testid="register-submit-btn">
              {loading ? "Creating…" : "Create account"}
            </button>
          </form>
          <div className="mt-6 text-sm text-ink-muted">
            Have an account? <Link to="/login" className="text-volt uppercase tracking-widest text-xs" data-testid="register-to-login-link">Sign in</Link>
          </div>
          <div className="mt-8 pt-6 border-t border-line flex flex-wrap gap-5 text-[10px] uppercase tracking-widest text-ink-dim">
            <Link to="/terms" className="hover:text-volt" data-testid="register-footer-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-volt" data-testid="register-footer-privacy">Privacy</Link>
            <Link to="/commercial-terms" className="hover:text-volt" data-testid="register-footer-commercial">Commercial</Link>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
